import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@mui/material";
import Dropdown from "../input/Dropdown";
import DateTimePickerInput from "../input/DateTimePickerInput";
import { formatFromJsonDate, isAfter, isBefore } from "../../dateConfig";
import { getDropdownData } from "../../api/keyvalue";
import LoadingSpinner from "../LoadingSpinner";
import Button from "../input/Button";
import { toast } from "react-toastify";
import {
  getCallsByJob,
  saveMissionControlCall,
} from "../../api/missioncontrolApi";
import {
  AsyncDropdownValueRenderer,
  AsyncOtherRefDropdownValueRenderer,
  ResizableScrollingTextRenderer,
} from "../renderer/StyleRenderer";
import Grid from "../Grid";
import { TextArea } from "semantic-ui-react";

const CallLog = ({ jobId }) => {
  const [errors, setErrors] = useState({});

  const [call, setCall] = useState({
    calJobId: jobId,
    calCallStartTime: "",
    calCallStartTimeFormatted: "",
    calCallEndTime: "",
    calCallEndTimeFormatted: "",
    calNotes: "",
    calCallType: 2,
    calCallStatus: 1,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dataLoadNeeded, setDataLoadNeeded] = useState(true);
  const [callTypeOptions, setCallTypeOptions] = useState([]);
  const [callStatusOptions, setCallStatusOptions] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [dateTimePickerReset, setDateTimePickerReset] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const callTypeOpt = await getDropdownData("call_type");
      setCallTypeOptions(callTypeOpt);
      const callStatusOpt = await getDropdownData("call_status");
      setCallStatusOptions(callStatusOpt);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (dataLoadNeeded) {
        setIsFetchLoading(true);
        const callsByJob = await getCallsByJob(jobId);
        setCallLogs(
          callsByJob.map((call) => ({
            ...call,
            calCallStartTimeFormatted: call.calCallStartTime
              ? formatFromJsonDate(call.calCallStartTimeFormatted)
              : "N/A",
            calCallEndTimeFormatted: call.calCallEndTime
              ? formatFromJsonDate(call.calCallEndTimeFormatted)
              : "N/A",
          })),
        );
        setDataLoadNeeded(false);
        setIsFetchLoading(false);
      }
    })();
  }, [dataLoadNeeded, jobId]);

  useEffect(() => {
    if (submitted) {
      setCall({
        calJobId: jobId,
        calCallStartTime: "",
        calCallStartTimeFormatted: "",
        calCallEndTime: "",
        calCallEndTimeFormatted: "",
        calNotes: "",
        calCallType: 2,
        calCallStatus: 1,
      });
      setSubmitted(false);
    }
  }, [jobId, submitted]);

  const submitHandler = async () => {
    if (!call.calCallType) {
      setErrors((prev) => ({ ...prev, calCallType: "Call Type is mandatory" }));
      return;
    }
    setIsSubmitLoading(true);
    try {
      await saveMissionControlCall({
        calJobId: call.calJobId,
        calCallStartTime: call.calCallStartTime,
        calCallEndTime: call.calCallEndTime,
        calCallType: call.calCallType,
        calNotes: call.calNotes,
        calCallStatus: call.calCallStatus,
      });
    } catch (e) {
      console.error(e);
      toast.error("Error adding action!", {
        autoClose: 3000,
      });
      setIsSubmitLoading(false);
      return;
    }
    setSubmitted(true);
    setDataLoadNeeded(true);
    setIsSubmitLoading(false);
  };

  return (
    <Card>
      <CardContent>
        {isLoading || isFetchLoading ? (
          <LoadingSpinner />
        ) : (
          <form className="ui form">
            <div className="ui segment">
              <div className="two fields ">
                <Dropdown
                  error={errors.calCallType}
                  clearable={false}
                  resetError={() =>
                    setErrors((prevState) => {
                      const { calCallType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  required
                  options={callTypeOptions}
                  label="Type"
                  value={call.calCallType}
                  onChange={(e, data) => {
                    setCall((prevState) => ({
                      ...prevState,
                      calCallType: data.value,
                    }));
                  }}
                />
                <Dropdown
                  error={errors.calCallStatus}
                  resetError={() =>
                    setErrors((prevState) => {
                      const { calCallStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={callStatusOptions}
                  label="Status"
                  value={call.calCallStatus}
                  onChange={(e, data) => {
                    setCall((prevState) => ({
                      ...prevState,
                      calCallStatus: data.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields ">
                <DateTimePickerInput
                  reset={dateTimePickerReset}
                  setReset={setDateTimePickerReset}
                  value={call.calCallStartTime}
                  onAccept={(value) => {
                    setCall((prevState) => ({
                      ...prevState,
                      calCallStartTime: value,
                      calCallStartTimeFormatted: formatFromJsonDate(value),
                    }));
                  }}
                  validators={[
                    {
                      func: (date) => isBefore(date, new Date()),
                      message:
                        "Date and Time should be before current date and time",
                    },
                    {
                      func: (date) =>
                        call.calCallEndTime
                          ? isBefore(date, call.calCallEndTime)
                          : true,
                      message:
                        "Date and Time should be before end date and time",
                    },
                  ]}
                  error={errors.calCallStartTime}
                  resetError={() =>
                    setErrors((prevState) => {
                      const { calCallStartTime: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  label="Call Start Time"
                />
                <DateTimePickerInput
                  reset={dateTimePickerReset}
                  setReset={setDateTimePickerReset}
                  value={call.calCallEndTime}
                  onAccept={(value) => {
                    setCall((prevState) => ({
                      ...prevState,
                      calCallEndTime: value,
                      calCallEndTimeFormatted: formatFromJsonDate(value),
                    }));
                  }}
                  validators={[
                    {
                      func: (date) => isBefore(date, new Date()),
                      message:
                        "Date and Time should be before current date and time",
                    },
                    {
                      func: (date) =>
                        call.calCallStartTime
                          ? isAfter(date, call.calCallStartTime)
                          : true,
                      message:
                        "Date and Time should be after start date and time",
                    },
                  ]}
                  error={errors.calCallEndTime}
                  resetError={() =>
                    setErrors((prevState) => {
                      const { calCallEndTime: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  label="Call End Time"
                />
              </div>
              <div className={`field ${errors.calNotes ? "error" : ""}`}>
                <label>Notes</label>
                <TextArea
                  placeholder="Notes"
                  value={call.calNotes}
                  onChange={(e) => {
                    setCall((prevState) => ({
                      ...prevState,
                      calNotes: e.target.value,
                    }));
                  }}
                />
                <small style={{ color: "#9f3a38" }}>{`${
                  errors.calNotes ? errors.calNotes : ""
                }`}</small>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                  gap: "5px",
                }}
              >
                <Button
                  template="submit"
                  onClick={submitHandler}
                  isLoading={isSubmitLoading}
                  label="Proceed"
                />
              </div>
            </div>
            <Grid
              heading="Stakeholder Clusters"
              config={[
                {
                  headerName: "Type",
                  field: "calCallType",
                  cellRenderer: AsyncDropdownValueRenderer,
                  cellRendererParams: {
                    dropdownKey: "call_type",
                  },
                },
                {
                  headerName: "Job",
                  field: "calJobId",
                  cellRenderer: AsyncOtherRefDropdownValueRenderer,
                  cellRendererParams: {
                    tableName: "transport.tms_t_job",
                    primaryKey: "job_id",
                    fieldName: "job_code",
                  },
                },
                {
                  headerName: "Status",
                  field: "calCallStatus",
                  cellRenderer: AsyncDropdownValueRenderer,
                  cellRendererParams: {
                    dropdownKey: "call_status",
                  },
                },
                {
                  headerName: "Start Time",
                  field: "calCallStartTimeFormatted",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "End Time",
                  field: "calCallEndTimeFormatted",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Notes",
                  field: "calNotes",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
              ]}
              content={callLogs}
            />
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default CallLog;
